import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Checkbox, Input, Popover, Tooltip } from "antd";
import { DownOutlined } from "@ant-design/icons";
import InfiniteScroll from "react-infinite-scroller";
import { useTranslation } from "react-i18next";
import styles from "./style.module.scss";

const CHUNK_SIZE = 50;

const countKeySelections = (selectionsForKey) => {
  if (!selectionsForKey || typeof selectionsForKey !== "object") return 0;
  return Object.keys(selectionsForKey).length;
};

const countAllSelections = (levelWiseSelections, level1Options) => {
  if (!levelWiseSelections || !level1Options) return 0;
  return level1Options.reduce((total, option) => {
    return total + countKeySelections(levelWiseSelections[option.id]);
  }, 0);
};

const hasAnySelection = (levelWiseSelections) => {
  if (!levelWiseSelections) return false;
  return Object.values(levelWiseSelections).some(
    (values) => values && Object.keys(values).length > 0
  );
};

const PivotTwoPaneFilter = ({
  title,
  levelWiseOptions,
  levelWiseSelections,
  setLevelWiseSelections,
  reset,
  apply,
  disabled,
  selectedFilterType,
  setSelectedFilterType,
  popupStyling,
  width,
  inline = false,
  hideOperatorToggle = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [valueSearch, setValueSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(CHUNK_SIZE);
  const scrollParentRef = useRef(null);

  const level1Options = levelWiseOptions?.level1 || [];

  useEffect(() => {
    if (!inline && !open) return;
    if (level1Options.length === 0) {
      if (activeKey != null) setActiveKey(null);
      return;
    }
    if (activeKey && level1Options.some((opt) => opt.id === activeKey)) return;
    setActiveKey(level1Options[0].id);
  }, [inline, open, level1Options, activeKey]);

  const handleToggleValue = useCallback(
    (key, valueId) => {
      setLevelWiseSelections((prev) => {
        const next = { ...prev };
        const keySelections = { ...(next[key] || {}) };
        if (keySelections[valueId]) {
          delete keySelections[valueId];
        } else {
          keySelections[valueId] = {};
        }
        if (Object.keys(keySelections).length === 0) {
          delete next[key];
        } else {
          next[key] = keySelections;
        }
        return next;
      });
    },
    [setLevelWiseSelections]
  );

  const valueOptions = useMemo(() => {
    if (!activeKey || !levelWiseOptions?.level2?.[activeKey]) return [];
    return levelWiseOptions.level2[activeKey];
  }, [activeKey, levelWiseOptions]);

  const filteredValueOptions = useMemo(() => {
    const query = valueSearch.trim().toLowerCase();
    if (!query) return valueOptions;
    return valueOptions.filter((option) =>
      String(option.label || option.id || "")
        .toLowerCase()
        .includes(query)
    );
  }, [valueOptions, valueSearch]);

  useEffect(() => {
    setVisibleCount(CHUNK_SIZE);
  }, [activeKey, valueSearch, filteredValueOptions.length]);

  const visibleItems = useMemo(
    () => filteredValueOptions.slice(0, visibleCount),
    [filteredValueOptions, visibleCount]
  );

  const hasMore = visibleCount < filteredValueOptions.length;

  const loadMore = useCallback(() => {
    if (visibleCount >= filteredValueOptions.length) return;
    setVisibleCount((prev) =>
      Math.min(prev + CHUNK_SIZE, filteredValueOptions.length)
    );
  }, [visibleCount, filteredValueOptions.length]);

  const totalSelected = countAllSelections(levelWiseSelections, level1Options);
  const showOperatorToggle = !hideOperatorToggle && totalSelected > 1;

  const panel = (
    <div
      className={popupStyling ? styles.panelPopup : styles.panel}
      style={width ? { width: `${width}px` } : undefined}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={styles.twoPane}>
        <div className={styles.keysPane}>
          <div className={styles.paneTitle}>
            {t("pivot_filter.pivots")}
          </div>
          <ul className={styles.keysList}>
            {level1Options.map((option) => {
              const selectedCount = countKeySelections(
                levelWiseSelections?.[option.id]
              );
              const isActive = option.id === activeKey;
              return (
                <li key={option.id}>
                  <button
                    type="button"
                    className={
                      isActive ? styles.keyItemActive : styles.keyItem
                    }
                    disabled={disabled}
                    onClick={() => {
                      setActiveKey(option.id);
                      setValueSearch("");
                    }}
                  >
                    <span className={styles.keyLabel}>{option.label}</span>
                    {selectedCount > 0 && (
                      <Badge
                        count={selectedCount}
                        className={styles.keyBadge}
                        color="#5b4bff"
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className={styles.valuesPane}>
          <div className={styles.valuesPaneHeader}>
            <div className={styles.paneTitle}>
              {activeKey || t("pivot_filter.values")}
            </div>
            <Input
              allowClear
              disabled={disabled}
              placeholder={`${t("button.5", "Search")}...`}
              value={valueSearch}
              onChange={(e) => setValueSearch(e.target.value)}
              className={styles.valueSearch}
            />
            <div className={styles.valueMeta}>
              {visibleItems.length} / {filteredValueOptions.length}
              {filteredValueOptions.length !== valueOptions.length &&
                ` (${valueOptions.length} total)`}
            </div>
          </div>

          {filteredValueOptions.length === 0 ? (
            <div className={styles.emptyValues}>
              {t("pivot_filter.no_match")}
            </div>
          ) : (
            <div ref={scrollParentRef} className={styles.valuesScroll}>
              <InfiniteScroll
                initialLoad={false}
                pageStart={0}
                loadMore={loadMore}
                hasMore={hasMore}
                useWindow={false}
                getScrollParent={() => scrollParentRef.current}
              >
                {visibleItems.map((option) => {
                  const checked = Boolean(
                    levelWiseSelections?.[activeKey]?.[option.id]
                  );
                  return (
                    <div key={option.id} className={styles.valueRow}>
                      <Checkbox
                        checked={checked}
                        disabled={disabled}
                        onChange={() =>
                          handleToggleValue(activeKey, option.id)
                        }
                      >
                        <span className={styles.valueLabel}>{option.label}</span>
                      </Checkbox>
                    </div>
                  );
                })}
              </InfiniteScroll>
            </div>
          )}
        </div>
      </div>

      {hasAnySelection(levelWiseSelections) && (
        <div className={styles.panelActions}>
          {showOperatorToggle && (
            <div className={styles.operatorToggle}>
              <Button
                size="small"
                className={
                  selectedFilterType === "AND"
                    ? styles.applyBtn
                    : styles.discardBtn
                }
                onClick={() => {
                  setSelectedFilterType("AND");
                  if (inline && apply) apply("AND");
                }}
              >
                <Tooltip title={t("filter.13")}> ∩ </Tooltip>
              </Button>
              <Button
                size="small"
                className={
                  selectedFilterType === "OR"
                    ? styles.applyBtn
                    : styles.discardBtn
                }
                onClick={() => {
                  setSelectedFilterType("OR");
                  if (inline && apply) apply("OR");
                }}
              >
                <Tooltip title={t("filter.14")}> ∪ </Tooltip>
              </Button>
            </div>
          )}
          <div className={styles.actionButtons}>
            <Button
              size="small"
              className={styles.discardBtn}
              disabled={disabled}
              onClick={() => {
                reset();
                if (!inline) setOpen(false);
              }}
            >
              {t("button.53")}
            </Button>
            {!inline ? (
              <Button
                size="small"
                type="primary"
                disabled={disabled}
                className={styles.applyBtn}
                onClick={() => {
                  apply(selectedFilterType);
                  setOpen(false);
                }}
              >
                {t("button.40")}
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );

  if (inline) {
    return (
      <div
        className={styles.wrapperInline}
        style={width ? { width: `${width}px`, maxWidth: "100%" } : undefined}
      >
        {panel}
      </div>
    );
  }

  return (
    <div
      className={popupStyling ? styles.wrapperPopup : styles.wrapper}
      style={width ? { width: `${width}px` } : undefined}
    >
      <Popover
        content={panel}
        trigger="click"
        open={open}
        onOpenChange={(nextOpen) => {
          if (disabled) return;
          setOpen(nextOpen);
          if (!nextOpen) setValueSearch("");
        }}
        placement="bottomLeft"
        overlayClassName={styles.popoverOverlay}
        destroyTooltipOnHide
      >
        <button
          type="button"
          className={popupStyling ? styles.triggerPopup : styles.trigger}
          disabled={disabled}
        >
          {title}
          <DownOutlined className={styles.triggerIcon} />
        </button>
      </Popover>
    </div>
  );
};

export default PivotTwoPaneFilter;
